import { exactInteger } from '@/lib/exact'
import type { Block, Cell, Column, TableBlock } from '@/kova/spec/types'
import type { ApiClient } from '@/lib/api'

import type { DataSource, ScreenData, ScreenLoader } from './provenance'
import { readAtStamp } from './provenance'

/**
 * Customers › Businesses (`cust-biz`), from `GET /v1/admin/businesses`.
 *
 * # A business is a customer, not a second notion of one
 *
 * `customers.kind = 'company'` and nothing else. There is no business table,
 * and this read is the customer list narrowed rather than a parallel register
 * that could disagree with it.
 *
 * # A business with no members is LISTED, and carries zero
 *
 * That is the ordinary state of one nobody has been invited to, and it is
 * precisely the row an operator came to find. Omitting it would hide the
 * businesses needing attention behind the ones that do not.
 *
 * # `owners` is counted apart from `live_members`, and the pair is the point
 *
 * Nothing in the schema forbids a business whose only owner was revoked. So
 * `live_members > 0` with `owners = 0` is a real and broken state — **people
 * who can look and nobody who can decide** — and it is invisible in a single
 * total. Both count memberships governing NOW; a revoked member is excluded,
 * because a historical count would report a business as staffed when it is not.
 *
 * # Acceptance is the institution's verdict, mirrored
 *
 * Never set here, never inferred (invariant 2). A business that is `pending` is
 * not a business we are refusing; it is one the institution has not answered on.
 *
 * # A member is an identity-provider subject and nothing else
 *
 * This backend holds no name, no email and no credential for them: authentication
 * is Ory's (§6), and no secret of theirs may enter this codebase (invariant 13).
 * So the screen shows WHO may act for a business and cannot show who they are —
 * and it says that on the page rather than leaving an empty column that reads as
 * a missing join.
 *
 * # Memberships are versioned and close-only
 *
 * A role change closes the row and opens the next version, so "who could
 * authorise this payment in March" is answerable only from the history
 * (invariant 9). Closed rows are the point rather than clutter. `live` is
 * SERVED rather than derived from `effective_to` — that derivation is the one a
 * client eventually gets wrong.
 *
 * # Read only
 *
 * No route invites, promotes or revokes a member, so no control is drawn and
 * none is drawn disabled.
 */

export const BUSINESSES_PATH = '/businesses'

const SOURCE = "postgres · customers where kind = 'company' (GET /v1/admin/businesses)"
const AUTHORITY = 'operational'

interface Business {
  readonly customerId: number | null
  readonly legalName: string
  readonly countryCode: string
  /** The INSTITUTION's verdict. Mirrored, never ours. */
  readonly acceptance: string
  /** Memberships governing NOW. Zero is real and common. */
  readonly liveMembers: number | null
  /** Live count at `owner`. Counted apart for a reason — see below. */
  readonly owners: number | null
  readonly createdAt: string
}

function readBusiness(raw: unknown): Business | null {
  if (typeof raw !== 'object' || raw === null) return null
  const o = raw as Record<string, unknown>
  const id = int(o['customer_id'])
  if (id === null) return null
  return {
    customerId: id,
    legalName: str(o['legal_name']),
    countryCode: str(o['country_code']),
    acceptance: str(o['acceptance']),
    liveMembers: int(o['live_members']),
    owners: int(o['owners']),
    createdAt: str(o['created_at']),
  }
}

/** True where people can act and nobody can decide. */
function ownerless(b: Business): boolean {
  return b.liveMembers !== null && b.owners !== null && b.liveMembers > 0 && b.owners === 0
}

const COLS: readonly Column[] = [
  ['#', 'm', '80px'],
  ['Legal name', 'x', 'minmax(200px,1.6fr)'],
  ['Country', 'm', '90px'],
  ['Accepted by the institution', 'c', 'minmax(180px,1.2fr)'],
  ['Who can act', 'c', 'minmax(220px,1.4fr)'],
  ['Registered', 'm', '120px'],
]

/**
 * Acceptance, as the institution's answer rather than ours.
 *
 * `pending` is not a refusal — it is an unanswered question, and rendering it
 * in a refusing tone would report a verdict nobody has given (invariant 2).
 */
function acceptanceCell(b: Business): Cell {
  if (b.acceptance === '') return '— not served'
  if (b.acceptance === 'accepted') return ['accepted', 'g']
  if (b.acceptance === 'rejected') return ['rejected', 'r']
  if (b.acceptance === 'more_info_required') return ['more info required', 'a']
  if (b.acceptance === 'pending') return ['pending — not yet answered', 'n']
  if (b.acceptance === 'in_review') return ['in review', 'n']
  return b.acceptance
}

/**
 * The two counts, and the broken state only the pair can show.
 *
 * A single total cannot distinguish a business nobody has joined from one whose
 * last owner was revoked. The first is ordinary; the second means nobody can
 * authorise anything for a company that has people looking at it.
 */
function membersCell(b: Business): Cell {
  if (b.liveMembers === null) return '— not served'
  if (ownerless(b)) {
    const n = String(b.liveMembers)
    return [`${n} member(s), NO owner — nobody can decide`, 'r']
  }
  if (b.liveMembers === 0) return ['nobody yet', 'n']
  const owners = b.owners === null ? '' : `, ${String(b.owners)} owner(s)`
  return `${String(b.liveMembers)} member(s)${owners}`
}

export function composeBusinesses(payload: Record<string, unknown>, readAt: Date): ScreenData {
  const raw = Array.isArray(payload['businesses'])
    ? (payload['businesses'] as readonly unknown[])
    : []
  const rows: Business[] = []
  let unreadable = 0
  for (const entry of raw) {
    const parsed = readBusiness(entry)
    if (parsed === null) unreadable += 1
    else rows.push(parsed)
  }

  const broken = rows.filter(ownerless)
  const empty = rows.filter((b) => b.liveMembers === 0).length

  const blocks: Block[] = []

  if (broken.length > 0) {
    blocks.push({
      t: 'note',
      tone: 'r',
      kicker: 'Nobody can decide',
      title: broken.length === 1
        ? 'One business has members who can act and no owner who can authorise'
        : `${String(broken.length)} businesses have members who can act and no owner who can authorise`,
      body: 'Nothing in the schema forbids a business whose last owner was revoked, so this is a state the system can reach and does not prevent. People can still look; nobody can decide. A single membership total cannot show it — it looks like an ordinarily staffed business — which is why the owner count is carried separately.',
      ref: 'live_members > 0 and owners = 0',
    })
  }

  const table: TableBlock = {
    t: 'table',
    title: 'Company customers',
    src: 'ledger',
    facets: false,
    cols: COLS,
    rows: rows.map((b) => [
      b.customerId === null ? '—' : String(b.customerId),
      b.legalName === '' ? '— NOT NAMED' : b.legalName,
      b.countryCode === '' ? '—' : b.countryCode,
      acceptanceCell(b),
      membersCell(b),
      b.createdAt === '' ? '—' : day(b.createdAt),
    ]),
  }
  blocks.push(table)

  blocks.push({
    t: 'note',
    tone: 'n',
    kicker: 'What a member is',
    title: 'This screen can show who may act for a business, and cannot show who they are',
    body: "A member is an identity-provider subject and nothing else. This backend holds no name, no email and no credential for them — authentication is the identity provider's, and no secret of theirs may enter this codebase. An operator who needs to know which person a subject belongs to resolves it in the identity provider. There is deliberately no join here that would put somebody's details on this row.",
    ref: 'invariant 13 · §6',
  })

  const missing: string[] = []
  if (unreadable > 0) missing.push(`${String(unreadable)} unreadable row(s)`)
  missing.push('who each member actually is — this backend holds only the identity-provider subject')
  missing.push('customers erased under Art. 17 — removed rather than redacted, so they are absent by design')

  const provenance: DataSource = {
    kind: 'partial', source: SOURCE, authority: AUTHORITY, readAt: readAtStamp(readAt), missing,
  }
  return {
    blocks,
    provenance,
    subject: `businesses · ${String(rows.length)} company customer(s), ${String(empty)} with nobody yet, ${String(broken.length)} with no owner`,
  }
}

export const loadBusinesses: ScreenLoader = async (api: ApiClient) => {
  let payload: unknown
  try {
    payload = await api.get(BUSINESSES_PATH)
  } catch (error) {
    if (error instanceof DOMException && error.name === 'AbortError') throw error
    return unreadable('The backend could not be reached.')
  }
  if (typeof payload !== 'object' || payload === null) {
    return unreadable('The business list returned a shape this console does not know.')
  }
  return composeBusinesses(payload as Record<string, unknown>, new Date())
}

function unreadable(reason: string): ScreenData {
  return {
    blocks: [{
      t: 'note',
      tone: 'r',
      kicker: 'Not read',
      title: 'The company customers could not be read',
      body: `${reason} Nothing is drawn. This is NOT an empty list: whether any business has people who can act and nobody who can authorise is unknown from here.`,
      ref: 'GET /v1/admin/businesses',
    }],
    provenance: { kind: 'unread', source: SOURCE, reason },
  }
}

function str(v: unknown): string {
  return typeof v === 'string' ? v : ''
}

function int(v: unknown): number | null {
  return exactInteger(v)
}

function day(stamp: string): string {
  const t = stamp.indexOf('T')
  return t === -1 ? stamp : stamp.slice(0, t)
}
